import React, { ReactNode } from "react";
import { Box, Button, Card, Chip, Slide, SxProps, Theme, Tooltip, Typography } from "@mui/material";
import { displayClusterType, displayDosage, displayPageType, LensHit, MatchingBlock, processCardItem } from "Backend";
import BlockWithSurroundings from "./HitCard/BlockWithSurroundings";
import { flatten, nonEmpty } from "helpers/coll";
import Flex from "components/Flex";
import { StateHandle } from "StateHandle";
import CardFormFragment from "./HitCard/CardFormFragment";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useFormCard } from "./useFormCard";
import PinToProfileButton from "./HitCard/PinToProfileButton";
import { ArrowForward, CheckCircle, RemoveCircleOutline } from "@mui/icons-material";
import { useAppSelector } from "redux/hooks";
import { selectIsOwner } from "redux/slices/session";
import theme from "theme";
import { P } from "theme/typography";
import { DocumentIntake } from "generated/graphql";
import LensHitActions from "./CaseHitsBrowser/LensHitActions";

const DocumentExcerptHighlightSX = (backgroundColor: string) => ({
  ".document-excerpt": {
    border: `1px solid ${theme.palette.grey[100]}`,
    ".MuiTypography-root": {
      backgroundColor,
    },
  },
});

interface SxStyles {
  childHitsDiv: SxProps<Theme>;
  mainCard: SxProps<Theme>;
  hoverPointer: SxProps<Theme>;
  selected: SxProps<Theme>;
  rootScroll: SxProps<Theme>;
  marginLeft: SxProps<Theme>;
  centeredText: SxProps<Theme>;
  cardNumberText: SxProps<Theme>;
  pinnedCheck: SxProps<Theme>;
  negationIcon: SxProps<Theme>;
}

const styles: SxStyles = {
  childHitsDiv: {
    display: "flex",
    flexDirection: "column",
    paddingTop: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    maxHeight: 300,
    overflow: "auto",
  },
  mainCard: {
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(1),
    width: "100%",
  },
  hoverPointer: {
    "&:hover": {
      cursor: "pointer",
      backgroundColor: theme.palette.grey["50"],
    },
  },
  selected: {
    borderColor: theme.palette.grey["500"],
    borderStyle: "solid",
    borderWidth: "2px",
  },
  rootScroll: {
    display: "flex",
    flexDirection: "column",
  },
  marginLeft: {
    marginLeft: theme.spacing(1),
  },
  centeredText: {
    ...theme.mixins.centeredTextLike,
  },
  cardNumberText: {
    fontSize: "10px",
    marginLeft: theme.spacing(1),
  },
  pinnedCheck: {
    color: theme.palette.grey["600"],
  },
  negationIcon: {
    color: theme.palette.grey["600"],
  },
};

export interface HitCardProps {
  lensId: number;
  caseId: number;
  intake: DocumentIntake | undefined;
  item: LensHit | MatchingBlock;
  childHits: LensHit[];
  setPageNum?: (pageNum: number) => void;
  selectedChildHitState: StateHandle<LensHit | undefined>;
  selected: boolean;
  showSurroundings: boolean;
  onClick?: () => unknown;
  idx: number;
  itemsFullCount: number;
  hitInForm?: boolean | undefined;
  isNegated?: boolean | undefined;
  onHitIgnored: (hit: LensHit) => void;
  isChild?: boolean;
  title?: string;
}

const HitCard = (props: HitCardProps) => {
  const [hideChildren, setHideChildren] = React.useState(true);
  const iAmOwner = useAppSelector(selectIsOwner);

  const {
    lensId,
    caseId,
    intake,
    item,
    selectedChildHitState: [selectedChildHit, setSelectedChildHit],
    selected,
    childHits,
    itemsFullCount,
    idx,
    hitInForm,
    isNegated,
    title,
    setPageNum,
    onHitIgnored,
    isChild,
  } = props;

  const { hit, entityOfItem, block, blockDate, hitOffsets, entitiesNoDupes } = processCardItem(item);

  const cancer = entityOfItem?.cancer;
  const cancerStage = entityOfItem?.cancerStage;
  const cancerType = entityOfItem?.cancerType;
  const cancerSubtype = entityOfItem?.cancerSubtype;
  const cancerRecurrence = entityOfItem?.cancerRecurrence;

  const medQuantity = entityOfItem?.medQuantity;
  const medTimesPerDay = entityOfItem?.medTimesPerDay;
  const medDaySupply = entityOfItem?.medDaySupply;

  const clusterType = item.clusterType;
  const pageType = item.pageType;
  const sectionText = clusterType
    ? displayClusterType(clusterType) + (pageType ? ", " + displayPageType(pageType) : "")
    : pageType
      ? displayPageType(pageType)
      : "";

  const firstLabel = hit?.pageLabels?.[0];

  const optionalFields: [string, string][] = flatten([
    pageType || clusterType ? ["Record Type", sectionText] : undefined,
    firstLabel ? ["Key Page", firstLabel] : undefined,
    entityOfItem?.entity.name ? ["Finding", entityOfItem?.entity.name] : undefined,
    entityOfItem?.dosage ? ["Dosage", displayDosage(entityOfItem?.dosage)] : undefined,
    medQuantity ? ["Quantity", medQuantity.toString()] : undefined,
    entityOfItem?.ndcCode ? ["NDC Code", entityOfItem?.ndcCode] : undefined,
    medTimesPerDay ? ["Times Per Day", medTimesPerDay.toString()] : undefined,
    medDaySupply ? ["Days Supply", medDaySupply.toString()] : undefined,
    cancer ? ["Cancer", cancer] : undefined,
    cancerType ? ["Cancer Type", cancerType] : undefined,
    cancerSubtype ? ["Cancer Subtype", cancerSubtype] : undefined,
    cancerStage ? ["Stage", cancerStage] : undefined,
    cancerRecurrence ? ["Recurrence", cancerRecurrence ? "Yes" : "No"] : undefined,
  ]);

  const formCard = useFormCard({
    lensId,
    caseId,
    intakeId: block.documentIntakeId,
    intakePage: block.originalPageNumber,
    blockIndex: block.index,
    initialFieldId: hit?.formFieldId,
    hit,
  });
  const {
    formModeState: [formMode, setFormMode],
    form,
    serverValues,
    savingFormState: [savingForm],
    fieldIdState,
    toggleFormMode,
    canSave,
  } = formCard;
  const [, setFieldId] = fieldIdState;

  React.useEffect(() => {
    setFieldId(hit?.formFieldId);
  }, [hit?.formFieldId]);

  React.useEffect(() => {
    setFormMode(false);
  }, [block.documentId, block.index, hit?.hitId]);

  const selectedCard = (
    <>
      {formMode && (
        <Flex flexGrow={1} flexDirection="column" justifyContent="space-between">
          <Slide in direction="up">
            <Box display="flex" flexGrow={1} flexDirection="column" justifyContent="space-between">
              {form && serverValues && (
                <CardFormFragment
                  useFormCard={formCard}
                  block={block}
                  entity={entityOfItem}
                  clusterType={clusterType}
                  pageType={pageType}
                  lensRuleDescription={hit?.ruleDescription}
                  lensRuleSubtype={hit?.ruleSubtype}
                  docPageCount={intake?.pages || 1}
                  showReferenceType={true}
                  showHighlightedText={false}
                  compact={true}
                  showNotes
                />
              )}
            </Box>
          </Slide>
        </Flex>
      )}
      <Flex
        paddingTop={1}
        flexDirection="row"
        justifyContent="space-between"
        sx={{
          width: "100%",
        }}>
        <Flex flexDirection="row" alignItems="center">
          {nonEmpty(props.childHits) && (
            <Button
              style={{ textTransform: "none" }}
              color="default"
              size="small"
              onClick={() => setHideChildren(!hideChildren)}
              startIcon={hideChildren ? <ArrowForward /> : <ExpandLessIcon />}>
              {hideChildren
                ? `View ${hit?.collapsedHits.length} similar ${
                    hit?.collapsedHits.length === 1 ? "finding" : "findings"
                  }`
                : "Hide similar findings"}
            </Button>
          )}
        </Flex>
        <Flex flexDirection="row" justifyContent="flex-end">
          {formMode && (
            <Button color="primary" size="small" onClick={() => setFormMode(false)}>
              Cancel
            </Button>
          )}
          {hit && !formMode && iAmOwner && !isChild && (
            <LensHitActions
              hit={hit}
              lensId={lensId}
              caseId={caseId}
              onHitIgnored={onHitIgnored}
              ignoreCollapsedHits={false}
              popoverOrigin={{
                anchorOrigin: {
                  vertical: "top",
                  horizontal: "right",
                },
                transformOrigin: {
                  vertical: "top",
                  horizontal: "right",
                },
              }}
            />
          )}
          <PinToProfileButton
            sx={{ mr: 1 }}
            formMode={formMode}
            canSave={canSave}
            isSaving={savingForm}
            toggleFormMode={toggleFormMode}
          />
        </Flex>
      </Flex>
    </>
  );

  const cardBody = (selected: boolean): ReactNode => {
    return (
      <>
        <Box
          sx={{
            position: "relative",
            "&:hover": DocumentExcerptHighlightSX(selected ? "#fff" : theme.palette.grey[50]),
          }}>
          <Box sx={{ display: "flex", px: 1 }}>
            {title && <P variant="caption">{title}</P>}
            <Box sx={{ flexGrow: 1 }} />
            <P variant="caption">{blockDate?.display()}</P>
          </Box>
          <P variant="body5" sx={{ textAlign: "right", display: "block", pr: 1, color: "grey.400" }}>
            {block.documentName} - Pg. {block.originalPageNumber}
          </P>
          <Box
            className="document-excerpt"
            sx={{
              display: "flex",
              justifyContent: "space-between",
              p: 1,
              border: `1px solid ${theme.palette.grey[50]}`,
              position: "relative",
            }}>
            <P
              variant="overline"
              sx={{
                color: "grey.400",
                backgroundColor: "#fff",
                position: "absolute",
                left: 16,
                top: -8,
                display: "block",
                px: 1,
              }}>
              Document Excerpt
            </P>

            <BlockWithSurroundings
              preSurroundings={item.preSurroundings}
              postSurroundings={item.postSurroundings}
              blockText={block.text}
              mainHit={hitOffsets}
              entities={entitiesNoDupes}
            />
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr minmax(65px, auto)" }}>
            <Box sx={{ display: "flex", flexWrap: "wrap", mt: 1 }}>
              {nonEmpty(optionalFields) &&
                !formMode &&
                optionalFields.map((field, idx) => (
                  <Chip key={idx} label={`${field[0]}: ${field[1]}`} sx={{ mr: 1, mb: 1 }} size="small" />
                ))}
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
              {hitInForm && (
                <Tooltip arrow title="In Profile">
                  <CheckCircle
                    sx={{
                      ...styles.pinnedCheck,
                      ml: 1,
                    }}
                  />
                </Tooltip>
              )}
              {isNegated && (
                <Tooltip arrow title="Negation Detected">
                  <RemoveCircleOutline
                    sx={{
                      ...styles.negationIcon,
                      ml: 1,
                    }}
                  />
                </Tooltip>
              )}
              <Typography sx={styles.cardNumberText}>
                {idx + 1} / {itemsFullCount}
              </Typography>
            </Box>
          </Box>
        </Box>
        {selected && selectedCard}
        {!selected && hit?.collapsedHits !== undefined && hit.collapsedHits.length > 0 && (
          <Box sx={{ pl: 1 }}>
            <P variant="xsSemiBold" sx={{ color: theme.palette.grey[400] }}>
              {hit.collapsedHits.length} similar {hit.collapsedHits.length === 1 ? "finding" : "findings"}
            </P>
          </Box>
        )}
      </>
    );
  };

  const clearOverrideHit = (hit: LensHit | undefined) => {
    if (setPageNum && hit && (!selected || selectedChildHit !== undefined)) setPageNum(hit.block.originalPageNumber);
    setSelectedChildHit(undefined);
  };

  const setOverrideHit = (hit: LensHit) => {
    if (setPageNum && selectedChildHit?.hitId !== hit.hitId) setPageNum(hit.block.originalPageNumber);
    setSelectedChildHit(hit);
  };

  const isTrulySelected = selected && (!selectedChildHit || selectedChildHit?.hitId === hit?.hitId);

  const styledCard = {
    ...styles.mainCard,
    ...(isTrulySelected
      ? {
          ...styles.selected,
          ...DocumentExcerptHighlightSX("#fff"),
        }
      : styles.hoverPointer),
  };

  return (
    <Flex width="100%" onClick={props.onClick}>
      <Card variant="outlined" sx={styledCard} key={block.index} onClick={() => clearOverrideHit(hit)}>
        {cardBody(isTrulySelected)}
      </Card>
      {!hideChildren && nonEmpty(childHits) && (
        <Slide direction="right" in={!hideChildren}>
          <Box sx={styles.childHitsDiv}>
            {/*^^ Above has to be a plain div because Slide needs a `ref` */}
            {childHits.map((hit, idx) => {
              const childSelected = selectedChildHit?.hitId === hit.hitId;
              return (
                <Flex key={idx} paddingBottom={1}>
                  <HitCard
                    isChild
                    onHitIgnored={onHitIgnored}
                    lensId={lensId}
                    caseId={caseId}
                    intake={intake}
                    item={hit}
                    childHits={[]}
                    selectedChildHitState={props.selectedChildHitState}
                    selected={childSelected}
                    onClick={() => {
                      setOverrideHit(hit);
                    }}
                    showSurroundings={false}
                    idx={idx}
                    itemsFullCount={childHits.length}
                  />
                </Flex>
              );
            })}
          </Box>
        </Slide>
      )}
    </Flex>
  );
};

export default HitCard;
